import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

const WEBINAR_DATE = new Date("2026-03-28T18:00:00");

export default function TopBar() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(getTimeLeft(WEBINAR_DATE));

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft(WEBINAR_DATE)), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, "0");

  const countdownBlocks = [
    { val: pad(timeLeft.days), label: "d" },
    { val: pad(timeLeft.hours), label: "h" },
    { val: pad(timeLeft.minutes), label: "m" },
    { val: pad(timeLeft.seconds), label: "s" },
  ];

  return (
    <div className="w-full bg-navy text-white">
      {/* ── Mobile layout: stacked two rows ── */}
      <div className="flex sm:hidden flex-col items-center justify-center py-2 px-4 gap-1">
        {/* Row 1: webinar link */}
        <Link
          to="/webinar"
          className="flex items-center gap-1.5 cursor-pointer hover:opacity-90"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
          </span>
          <span className="text-white text-xs font-semibold whitespace-nowrap">
            Next live demo starting soon
          </span>
          <i className="ri-arrow-right-line text-secondary text-xs" />
        </Link>

        {/* Row 2: countdown */}
        <div className="flex items-center gap-1">
          <span className="text-white/50 text-[10px] mr-0.5">Starts in:</span>
          {countdownBlocks.map((item, i) => (
            <div key={i} className="flex items-center gap-0.5">
              <div className="bg-white/10 rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold min-w-[22px] text-center">
                {item.val}
              </div>
              <span className="text-white/40 text-[10px]">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Desktop layout: single row ── */}
      <div className="hidden sm:flex items-center justify-between py-2.5 px-4 gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-fill text-primary text-xs" />
          </span>
          <span className="text-white/80 text-sm whitespace-nowrap overflow-hidden text-ellipsis">
            <strong className="text-white font-semibold">SOC 2 Type II Certified</strong> AI Calling Infrastructure
            &nbsp;|&nbsp; Live Webinar: See It In Action —&nbsp;
            <Link to="/webinar" className="text-secondary font-semibold hover:underline cursor-pointer whitespace-nowrap">
              Register Now
            </Link>
          </span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-white/60 text-xs mr-1 hidden md:inline">Webinar starts in:</span>
          {countdownBlocks.map((item, i) => (
            <div key={i} className="flex items-center gap-0.5">
              <div className="bg-white/10 rounded px-1.5 py-0.5 text-xs font-mono font-semibold min-w-[26px] text-center">
                {item.val}
              </div>
              <span className="text-white/50 text-xs">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
